/**
 * WS5 #10 Stage B B5(a): a native, AngularJS-free port of the `$sanitize` service the
 * account app actually bundles — textAngular's `textAngular-sanitize` (a fork of
 * AngularJS 1.3.10 `ngSanitize`, the build loaded by `angularjs.module.ts:18`). It is
 * BYTE-FOR-BYTE faithful to that implementation: same HTML tokeniser, same
 * whitelist-driven writer, same `encodeEntities` / browser-`<pre>` `decodeEntities`, same
 * `validStyles` / `validCustomTag` (textAngular's additions), and the same `$$sanitizeUri`
 * URL allow-list (ported from AngularJS core, used for href/src — corpus-unreachable but
 * kept for arbitrary interpolated params).
 *
 * This replaces the lazy `$injector.get('$sanitize')` bridge in
 * `AngularTranslateCompatParser.sanitize()` — the SINGLE remaining translate↔AngularJS tie
 * — so native translation renders survive the B6 boot-stop. Byte equivalence over the WHOLE
 * `en` dictionary (incl. the 21 entries carrying `<b>`/`<br>`/`<br/>`/`<strong>`) is proven
 * continuously by `account-parity/_diag-translate-equiv.mjs` (live AngularJS
 * `$filter('translate')` vs `window.__ngxTranslate.instant` — 0 mismatches).
 *
 * The substituted AngularJS helpers (verbatim semantics): `angular.lowercase` →
 * `$$lowercase` (strings only — see angularjs.module.ts:8), `angular.forEach` over an attrs
 * object → own-key iteration in insertion order, `angular.extend` → Object.assign,
 * `angular.bind(buf, buf.push)` → a push closure, `angular.noop` → a no-op.
 */

/* eslint-disable */

function $sanitizeMinErr(code: string, msg: string, arg: string): Error {
  return new Error('[$sanitize:' + code + '] ' + msg.replace('{0}', arg));
}

// `angular.$$lowercase` (angularjs.module.ts re-points `angular.lowercase` to it):
// lowercases STRINGS only, returns non-strings untouched.
function lc(value: any): any {
  return typeof value === 'string' ? value.toLowerCase() : value;
}

// Regular Expressions for parsing tags and attributes (verbatim).
const START_TAG_REGEXP =
       /^<((?:[a-zA-Z])[\w:-]*)((?:\s+[\w:-]+(?:\s*=\s*(?:(?:"[^"]*")|(?:'[^']*')|[^>\s]+))?)*)\s*(\/?)\s*(>?)/,
  END_TAG_REGEXP = /^<\/\s*([\w:-]+)[^>]*>/,
  ATTR_REGEXP = /([\w:-]+)(?:\s*=\s*(?:(?:"((?:[^"])*)")|(?:'((?:[^'])*)')|([^>\s]+)))?/g,
  BEGIN_TAG_REGEXP = /^</,
  BEGING_END_TAGE_REGEXP = /^<\//,
  COMMENT_REGEXP = /<!--(.*?)-->/g,
  SINGLE_COMMENT_REGEXP = /(^<!--.*?-->)/,
  DOCTYPE_REGEXP = /<!DOCTYPE([^>]*?)>/i,
  CDATA_REGEXP = /<!\[CDATA\[(.*?)]]>/g,
  SURROGATE_PAIR_REGEXP = /[\uD800-\uDBFF][\uDC00-\uDFFF]/g,
  // Match everything outside of normal chars and " (quote character)
  NON_ALPHANUMERIC_REGEXP = /([^\#-~| |!])/g,
  WHITE_SPACE_REGEXP = /^(\s+)/;

function makeMap(str: string): { [k: string]: boolean } {
  const obj: { [k: string]: boolean } = {};
  const items = str.split(',');
  for (let i = 0; i < items.length; i++) obj[items[i]] = true;
  return obj;
}

// Safe Void Elements - HTML5
const voidElements = makeMap('area,br,col,hr,img,wbr,input');

// Elements that you can, intentionally, leave open (and which close themselves)
const optionalEndTagBlockElements = makeMap('colgroup,dd,dt,li,p,tbody,td,tfoot,th,thead,tr'),
  optionalEndTagInlineElements = makeMap('rp,rt'),
  optionalEndTagElements = Object.assign({}, optionalEndTagInlineElements, optionalEndTagBlockElements);

// Safe Block Elements - HTML5
const blockElements = Object.assign({}, optionalEndTagBlockElements, makeMap('address,article,' +
  'aside,blockquote,caption,center,del,dir,div,dl,figure,figcaption,footer,h1,h2,h3,h4,h5,' +
  'h6,header,hgroup,hr,ins,map,menu,nav,ol,pre,script,section,table,ul'));

// Inline Elements - HTML5
const inlineElements = Object.assign({}, optionalEndTagInlineElements, makeMap('a,abbr,acronym,b,' +
  'bdi,bdo,big,br,cite,code,del,dfn,em,font,i,img,ins,kbd,label,map,mark,q,ruby,rp,rt,s,' +
  'samp,small,span,strike,strong,sub,sup,time,tt,u,var'));

// SVG Elements
const svgElements = makeMap('animate,animateColor,animateMotion,animateTransform,circle,defs,' +
  'desc,ellipse,font-face,font-face-name,font-face-src,g,glyph,hkern,image,linearGradient,' +
  'line,marker,metadata,missing-glyph,mpath,path,polygon,polyline,radialGradient,rect,set,' +
  'stop,svg,switch,text,title,tspan,use');

// Special Elements (can contain anything)
const specialElements = makeMap('script,style');

const validElements = Object.assign({}, voidElements, blockElements, inlineElements, optionalEndTagElements, svgElements);

// Attributes that have href and hence need to be sanitized
const uriAttrs = makeMap('background,cite,href,longdesc,src,usemap,xlink:href');

const htmlAttrs = makeMap('abbr,align,alt,axis,bgcolor,border,cellpadding,cellspacing,class,clear,' +
  'color,cols,colspan,compact,coords,dir,face,headers,height,hreflang,hspace,' +
  'id,ismap,lang,language,nohref,nowrap,rel,rev,rows,rowspan,rules,' +
  'scope,scrolling,shape,size,span,start,summary,target,title,type,' +
  'valign,value,vspace,width');

const svgAttrs = makeMap('accent-height,accumulate,additive,alphabetic,arabic-form,ascent,' +
  'attributeName,attributeType,baseProfile,bbox,begin,by,calcMode,cap-height,class,color,' +
  'color-rendering,content,cx,cy,d,dx,dy,descent,display,dur,end,fill,fill-rule,font-family,' +
  'font-size,font-stretch,font-style,font-variant,font-weight,from,fx,fy,g1,g2,glyph-name,' +
  'gradientUnits,hanging,height,horiz-adv-x,horiz-origin-x,ideographic,k,keyPoints,' +
  'keySplines,keyTimes,lang,marker-end,marker-mid,marker-start,markerHeight,markerUnits,' +
  'markerWidth,mathematical,max,min,offset,opacity,orient,origin,overline-position,' +
  'overline-thickness,panose-1,path,pathLength,points,preserveAspectRatio,r,refX,refY,' +
  'repeatCount,repeatDur,requiredExtensions,requiredFeatures,restart,rotate,rx,ry,slope,stemh,' +
  'stemv,stop-color,stop-opacity,strikethrough-position,strikethrough-thickness,stroke,' +
  'stroke-dasharray,stroke-dashoffset,stroke-linecap,stroke-linejoin,stroke-miterlimit,' +
  'stroke-opacity,stroke-width,systemLanguage,target,text-anchor,to,transform,type,u1,u2,' +
  'underline-position,underline-thickness,unicode,unicode-range,units-per-em,values,version,' +
  'viewBox,visibility,width,widths,x,x-height,x1,x2,xlink:actuate,xlink:arcrole,xlink:role,' +
  'xlink:show,xlink:title,xlink:type,xml:base,xml:lang,xml:space,xmlns,xmlns:xlink,y,y1,y2,' +
  'zoomAndPan');

const validAttrs = Object.assign({}, uriAttrs, svgAttrs, htmlAttrs);

interface SanitizeHandler {
  start?: (tag: string, attrs: { [k: string]: string }, unary: boolean) => void;
  end?: (tag: string) => void;
  chars?: (text: string) => void;
  comment?: (text: string) => void;
  whitespace?: (text: string) => void;
}

function htmlParser(html: any, handler: SanitizeHandler): void {
  if (typeof html !== 'string') {
    if (html === null || typeof html === 'undefined') {
      html = '';
    } else {
      html = '' + html;
    }
  }
  let index: number, chars: boolean, match: any, text: string;
  const stack: any = [];
  let last = html;
  stack.last = function () { return stack[stack.length - 1]; };

  while (html) {
    text = '';
    chars = true;

    // Make sure we're not in a script or style element
    if (!stack.last() || !specialElements[stack.last()]) {

      // White space
      if (WHITE_SPACE_REGEXP.test(html)) {
        match = html.match(WHITE_SPACE_REGEXP);
        if (match) {
          if (handler.whitespace) handler.whitespace(match[0]);
          html = html.replace(match[0], '');
          chars = false;
        }
        // Comment
      } else if (SINGLE_COMMENT_REGEXP.test(html)) {
        match = html.match(SINGLE_COMMENT_REGEXP);
        if (match) {
          if (handler.comment) handler.comment(match[1]);
          html = html.replace(match[0], '');
          chars = false;
        }
        // DOCTYPE
      } else if (DOCTYPE_REGEXP.test(html)) {
        match = html.match(DOCTYPE_REGEXP);
        if (match) {
          html = html.replace(match[0], '');
          chars = false;
        }
        // end tag
      } else if (BEGING_END_TAGE_REGEXP.test(html)) {
        match = html.match(END_TAG_REGEXP);
        if (match) {
          html = html.substring(match[0].length);
          match[0].replace(END_TAG_REGEXP, parseEndTag);
          chars = false;
        }
        // start tag
      } else if (BEGIN_TAG_REGEXP.test(html)) {
        match = html.match(START_TAG_REGEXP);
        if (match) {
          // We only have a valid start-tag if there is a '>'.
          if (match[4]) {
            html = html.substring(match[0].length);
            match[0].replace(START_TAG_REGEXP, parseStartTag);
          }
          chars = false;
        } else {
          // no ending tag found --- this piece should be encoded as an entity.
          text += '<';
          html = html.substring(1);
        }
      }

      if (chars) {
        index = html.indexOf('<');
        text += index < 0 ? html : html.substring(0, index);
        html = index < 0 ? '' : html.substring(index);
        if (handler.chars) handler.chars(decodeEntities(text));
      }

    } else {
      // We're in a script or style element.
      html = html.replace(new RegExp('([^]*)<\\s*\\/\\s*' + stack.last() + '[^>]*>', 'i'),
        function (all: string, t: string) {
          t = t.replace(COMMENT_REGEXP, '$1').replace(CDATA_REGEXP, '$1');
          if (handler.chars) handler.chars(decodeEntities(t));
          return '';
        });
      parseEndTag('', stack.last());
    }

    if (html == last) {
      throw $sanitizeMinErr('badparse', 'The sanitizer was unable to parse the following block ' +
        'of html: {0}', html);
    }
    last = html;
  }

  // Clean up any remaining tags
  parseEndTag();

  function parseStartTag(tag: string, tagName: string, rest: string, unary: any): void {
    tagName = lc(tagName);
    if (blockElements[tagName]) {
      while (stack.last() && inlineElements[stack.last()]) {
        parseEndTag('', stack.last());
      }
    }

    if (optionalEndTagElements[tagName] && stack.last() == tagName) {
      parseEndTag('', tagName);
    }

    unary = voidElements[tagName] || !!unary;

    if (!unary)
      stack.push(tagName);

    const attrs: { [k: string]: string } = {};

    rest.replace(ATTR_REGEXP,
      function (m: string, name: string, doubleQuotedValue: string, singleQuotedValue: string, unquotedValue: string): string {
        const value = doubleQuotedValue
          || singleQuotedValue
          || unquotedValue
          || '';
        attrs[name] = decodeEntities(value);
        return '';
      });
    if (handler.start) handler.start(tagName, attrs, unary);
  }

  function parseEndTag(tag?: string, tagName?: string): void {
    let pos = 0, i: number;
    tagName = lc(tagName);
    if (tagName)
      // Find the closest opened tag of the same type
      for (pos = stack.length - 1; pos >= 0; pos--)
        if (stack[pos] == tagName)
          break;

    if (pos >= 0) {
      // Close all the open elements, up the stack
      for (i = stack.length - 1; i >= pos; i--)
        if (handler.end) handler.end(stack[i]);

      // Remove the open elements from the stack
      stack.length = pos;
    }
  }
}

let hiddenPre: HTMLPreElement | null = null;
const spaceRe = /^(\s*)([\s\S]*?)(\s*)$/;
function decodeEntities(value: string): string {
  if (!value) { return ''; }
  if (!hiddenPre) hiddenPre = document.createElement('pre');

  // Capture leading/trailing whitespace (IE8 innerHTML quirk parity) and reattach.
  const parts = spaceRe.exec(value) as RegExpExecArray;
  const spaceBefore = parts[1];
  const spaceAfter = parts[3];
  let content = parts[2];
  if (content) {
    hiddenPre.innerHTML = content.replace(/</g, '&lt;');
    content = 'textContent' in hiddenPre ? (hiddenPre.textContent as string) : (hiddenPre as any).innerText;
  }
  return spaceBefore + content + spaceAfter;
}

function encodeEntities(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(SURROGATE_PAIR_REGEXP, function (v: string) {
      const hi = v.charCodeAt(0);
      const low = v.charCodeAt(1);
      return '&#' + (((hi - 0xD800) * 0x400) + (low - 0xDC00) + 0x10000) + ';';
    })
    .replace(NON_ALPHANUMERIC_REGEXP, function (v: string) {
      const c = v.charCodeAt(0);
      // if unsafe character encode
      if (c <= 159 ||
        c == 173 ||
        (c >= 1536 && c <= 1540) ||
        c == 1807 ||
        c == 6068 ||
        c == 6069 ||
        (c >= 8204 && c <= 8207) ||
        (c >= 8232 && c <= 8239) ||
        (c >= 8288 && c <= 8303) ||
        c == 65279 ||
        (c >= 65520 && c <= 65535)) return '&#' + c + ';';
      return v; // avoids multilingual issues
    })
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function trim(value: any): any {
  return typeof value === 'string' ? value.trim() : value;
}

// textAngular custom style allow-list (color/background-color/text-align/text-decoration/
// font-weight/font-style/float/vertical-align/font-size/width/height/direction).
function validStyles(styleAttr: string): string {
  let result = '';
  const styleArray = styleAttr.split(';');
  styleArray.forEach(function (value) {
    const v = value.split(':');
    if (v.length == 2) {
      const key = trim(lc(v[0]));
      const val = trim(lc(v[1]));
      if (
        (key === 'color' || key === 'background-color') && (
          val.match(/^rgb\([0-9%,\. ]*\)$/i)
          || val.match(/^rgba\([0-9%,\. ]*\)$/i)
          || val.match(/^hsl\([0-9%,\. ]*\)$/i)
          || val.match(/^hsla\([0-9%,\. ]*\)$/i)
          || val.match(/^#[0-9a-f]{3,6}$/i)
          || val.match(/^[a-z]*$/i)
        )
        ||
        key === 'text-align' && (
          val === 'left' || val === 'right' || val === 'center' || val === 'justify'
        )
        ||
        key === 'text-decoration' && (
          val === 'underline' || val === 'line-through'
        )
        ||
        key === 'font-weight' && (
          val === 'bold'
        )
        ||
        key === 'font-style' && (
          val === 'italic'
        )
        ||
        key === 'float' && (
          val === 'left' || val === 'right' || val === 'none'
        )
        ||
        key === 'vertical-align' && (
          val === 'baseline' || val === 'sub' || val === 'super' || val === 'test-top'
          || val === 'text-bottom' || val === 'middle' || val === 'top' || val === 'bottom'
          || val.match(/[0-9]*(px|em)/)
          || val.match(/[0-9]+?%/)
        )
        ||
        key === 'font-size' && (
          val === 'xx-small' || val === 'x-small' || val === 'small' || val === 'medium'
          || val === 'large' || val === 'x-large' || val === 'xx-large' || val === 'larger'
          || val === 'smaller'
          || val.match(/[0-9]*\.?[0-9]*(px|em|rem|mm|q|cm|in|pt|pc|%)/)
        )
        ||
        (key === 'width' || key === 'height') && (
          val.match(/[0-9\.]*(px|em|rem|%)/)
        )
        || // Reference #520
        (key === 'direction' && val.match(/^ltr|rtl|initial|inherit$/))
      ) result += key + ': ' + val + ';';
    }
  });
  return result;
}

// textAngular: manually allow specific attributes on specific tags (the iframe video shim).
function validCustomTag(tag: string, attrs: { [k: string]: string }, lkey: string, value: string): boolean {
  if (tag === 'img' && attrs['ta-insert-video']) {
    if (lkey === 'ta-insert-video' || lkey === 'allowfullscreen' || lkey === 'frameborder' || (lkey === 'contenteditable' && value === 'false')) return true;
  }
  return false;
}

// AngularJS core `$$sanitizeUri` (the href/src allow-list). Corpus-unreachable (no uriAttrs
// in the dictionary) but kept for arbitrary interpolated params, byte-faithful to core.
const aHrefSanitizationWhitelist = /^\s*(https?|ftp|mailto|tel|file):/;
const imgSrcSanitizationWhitelist = /^\s*((https?|ftp|file|blob):|data:image\/)/;
let urlParsingNode: HTMLAnchorElement | null = null;
function urlResolveHref(url: string): string {
  if (!urlParsingNode) urlParsingNode = document.createElement('a');
  urlParsingNode.setAttribute('href', url);
  return urlParsingNode.href;
}
function sanitizeUri(uri: string, isImage: boolean): string {
  const regex = isImage ? imgSrcSanitizationWhitelist : aHrefSanitizationWhitelist;
  const normalizedVal = urlResolveHref(uri);
  if (normalizedVal !== '' && !normalizedVal.match(regex)) {
    return 'unsafe:' + normalizedVal;
  }
  return uri;
}

function htmlSanitizeWriter(buf: string[], uriValidator: (uri: string, isImage: boolean) => boolean): SanitizeHandler {
  let ignore: any = false;
  const out = (x: string) => { buf.push(x); };
  return {
    start: function (tag: string, attrs: { [k: string]: string }, unary: boolean) {
      tag = lc(tag);
      if (!ignore && specialElements[tag]) {
        ignore = tag;
      }
      if (!ignore && validElements[tag] === true) {
        out('<');
        out(tag);
        Object.keys(attrs).forEach(function (key) {
          let value = attrs[key];
          const lkey = lc(key);
          const isImage = (tag === 'img' && lkey === 'src') || (lkey === 'background');
          if ((lkey === 'style' && (value = validStyles(value)) !== '') || validCustomTag(tag, attrs, lkey, value) || validAttrs[lkey] === true &&
            (uriAttrs[lkey] !== true || uriValidator(value, isImage))) {
            out(' ');
            out(key);
            out('="');
            out(encodeEntities(value));
            out('"');
          }
        });
        out(unary ? '/>' : '>');
      }
    },
    comment: function (com: string) {
      out(com);
    },
    whitespace: function (ws: string) {
      out(encodeEntities(ws));
    },
    end: function (tag: string) {
      tag = lc(tag);
      if (!ignore && validElements[tag] === true) {
        out('</');
        out(tag);
        out('>');
      }
      if (tag == ignore) {
        ignore = false;
      }
    },
    chars: function (chars: string) {
      if (!ignore) {
        out(encodeEntities(chars));
      }
    },
  };
}

/**
 * Native equivalent of the bundled textAngular `$sanitize(html)`. Parses the input into
 * tokens, re-serialises only allow-listed tokens through `htmlSanitizeWriter`. Byte-identical
 * to the AngularJS service for the translation dictionary corpus (proven by the equivalence
 * sweep). The optional version-marker second-arg of `$SanitizeProvider` is unused by the
 * translate 'sanitize' strategy (single-arg call), so it is omitted.
 */
export function ngSanitize(html: string): string {
  const buf: string[] = [];
  htmlParser(html, htmlSanitizeWriter(buf, function (uri: string, isImage: boolean) {
    return !/^unsafe/.test(sanitizeUri(uri, isImage));
  }));
  return buf.join('');
}
